/**
 * This file is made for formatting data from the OneSearch request to a format our propertyCard expects
 */

import { Image } from '@awazevr/design-system.compositions.image-carousel-overlay'
import {
  Deposit,
  getActiveDepositScheme,
} from '@awazevr/mfe-common.configs.brand'
import type {
  AccommodationType,
  UniqueSellingPoint,
} from '@awazevr/mfe-common.types-and-mocks.site-search-service'
import { formatPropertyPageURL } from '@awazevr/mfe-common.utils.pdp-url'

import {
  DEFAULT_IMAGE_URL,
  IMAGE_DOMAIN_BASE_URL,
  IMAGE_DOMAIN_NOVASOL,
  PRICE_FROM_NIGHTS,
} from '@/constants'
import { getEndDateFromStartDateDuration } from '@/utils/getEndDateFromStartDateDuration'
import { isNovasolPoweredBrand } from '@/utils/isNovasolPoweredBrand'

import { removeFloorplanImagesFromImageArray } from './helpers'
import type {
  Brand,
  PropertyDetailLinkProps,
  PropertyFormatterProps,
  PropertyFormatterReturnProps,
  PropertyImageOSProps,
  SetActiveDepositSchemeProps,
} from './propertyFormatter.types'

export const getDecodedText = (text: string | undefined): string | null => {
  if (!text) return null

  const parser = new DOMParser()
  const doc = parser.parseFromString(text, 'text/html')
  const returnString = doc.documentElement.textContent

  if (returnString === null) return null

  return returnString
}

export const getParsedNumber = (
  value: string | number | undefined,
): number | null => {
  if (value === undefined || !value) return null
  const parsedNumber = parseFloat(value.toString())
  if (isNaN(parsedNumber)) {
    return null
  }
  return parsedNumber
}

export const getFormattedDate = (
  input: string | undefined,
  isWrongWayRound: boolean,
): Date | null => {
  if (!input) return null

  try {
    if (isWrongWayRound) {
      const [dayStr, monthStr, yearStr] = input.split('-')

      const day = parseInt(dayStr)
      const month = parseInt(monthStr)
      const year = parseInt(yearStr)

      if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year))
        return null

      const date = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0))
      return isNaN(date.getTime()) ? null : date
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
      const date = new Date(`${input}T00:00:00Z`)
      return isNaN(date.getTime()) ? null : date
    }

    const date = new Date(input)

    return isNaN(date.getTime()) ? null : date
  } catch {
    return null
  }
}

export const setImageAttributes = (
  images: PropertyImageOSProps[] | undefined,
  brand: Brand,
): Array<Image> => {
  if (images === undefined || images.length === 0)
    return [
      {
        src: DEFAULT_IMAGE_URL,
        alt: getDecodedText('Image coming soon') ?? '',
      },
    ]

  const imagesWithoutFloorplans = images
    ? removeFloorplanImagesFromImageArray(images)
    : []

  const formattedImageArray = imagesWithoutFloorplans.map(
    (image: PropertyImageOSProps) => {
      let imageURI

      if (isNovasolPoweredBrand(brand)) {
        const propertySuffix = image.assetId.split('/')[1]
        imageURI = `${IMAGE_DOMAIN_NOVASOL}/pic/600/${propertySuffix.substring(0, 3)}/${propertySuffix}`
      } else {
        imageURI = `${IMAGE_DOMAIN_BASE_URL}/${image.assetId.substring(0, 3)}/640/${image.assetId}.jpg`
      }

      return {
        src: imageURI,
        alt: getDecodedText(image.caption) ?? image.caption,
      }
    },
  )

  return formattedImageArray
}

export const getArrayValues = <T>(
  values: Array<T> | undefined,
): Array<T> | null => {
  if (!values?.length) return null

  return values
}

export const setActiveDepositScheme = ({
  cardinality,
  price,
  priceFrom,
  sleeps,
  startDate,
  country,
  accommodationType,
  deposits,
  lengthOfStay,
}: SetActiveDepositSchemeProps): Deposit | null => {
  if (startDate === null || deposits === null) return null

  let priceForActiveDepositScheme

  if (cardinality === 'SINGLE' && price) {
    priceForActiveDepositScheme = price
  } else {
    priceForActiveDepositScheme = priceFrom
  }

  const sleepsForActiveDepositScheme =
    cardinality === 'SINGLE' && sleeps ? sleeps : 0

  const dateToday = new Date()
  dateToday.setUTCHours(0, 0, 0, 0)

  const startDateInst = new Date(startDate)
  const endDateInst = getEndDateFromStartDateDuration(
    startDateInst,
    lengthOfStay,
  )
  const normalizedCountry = country?.toUpperCase()

  const activeDepositScheme =
    startDate &&
    getActiveDepositScheme(
      deposits,
      dateToday,
      startDateInst,
      endDateInst,
      sleepsForActiveDepositScheme,
      priceForActiveDepositScheme ?? 0,
      accommodationType,
      normalizedCountry,
    )

  if (activeDepositScheme) {
    return activeDepositScheme
  } else {
    return null
  }
}

export const setPropertyDetailLink = ({
  lengthOfStay,
  title,
  code,
  siteName,
  siteCode,
  pdpUrlSegment,
  searchParameters,
  startDate,
}: PropertyDetailLinkProps): string => {
  lengthOfStay && searchParameters.set('nights', lengthOfStay.toString())

  if (startDate) {
    const day = String(startDate.getUTCDate()).padStart(2, '0')
    const month = String(startDate.getUTCMonth() + 1).padStart(2, '0')
    const year = String(startDate.getUTCFullYear())
    searchParameters.set('start', `${day}-${month}-${year}`)
  }

  const formattedParams = searchParameters.toString().replace(/%21/g, '') // HACK: Remove ! (ideally should be fixed in fusion)

  const pageURL = formatPropertyPageURL(
    pdpUrlSegment,
    title || '',
    code,
    formattedParams,
    siteName,
    siteCode,
  )

  return pageURL
}

const setExperimentsValues = ({
  formattedProps,
  searchParams,
  datedSearch,
  brand,
}: {
  formattedProps: PropertyFormatterReturnProps
  searchParams: URLSearchParams
  datedSearch: boolean
  brand: Brand
}) => {
  if (!datedSearch && brand !== 'novasol') {
    formattedProps.wasPrice = null
    formattedProps.startDate = null
  }

  if (
    searchParams.get('accommodationType') === 'holiday-parks' ||
    searchParams.get('accommodationType') === 'lodges'
  ) {
    formattedProps.startDate = null
  }

  return formattedProps
}

interface HandleRh3AndPropertyLocationParams {
  rhs3: string | undefined
  propertyLocation: string | undefined
}

const handleRh3AndPropertyLocation = ({
  rhs3,
  propertyLocation,
}: HandleRh3AndPropertyLocationParams) => {
  return [propertyLocation, rhs3]
    .filter((value) => !!value)
    .map((value) => getDecodedText(value))
    .join(', ')
}

const getKSPArray = (KSPs: Array<UniqueSellingPoint>): string[] => {
  const kspArray: string[] = []

  KSPs.forEach((ksp) => {
    const decodedKSP = getDecodedText(ksp.value)
    if (decodedKSP) {
      kspArray.push(decodedKSP)
    }
  })

  return kspArray
}

export const formatter = ({
  property,
  brand,
  propertyCardV2Variant,
  deposits,
  searchParams,
  datedSearch,
  pdpUrlSegment,
  country,
  formatDate,
  formatMessage,
  startDate,
  siteSearch,
}: PropertyFormatterProps): PropertyFormatterReturnProps => {
  const accommodationType = searchParams.get('accommodationType') || 'accom'

  const newStartDate =
    siteSearch && startDate
      ? getFormattedDate(startDate, true)
      : getFormattedDate(property.startDate, false) || null

  const isNovasolBrand = isNovasolPoweredBrand(brand)
  let PDPUrlStartDate = datedSearch ? newStartDate : null

  let propertyName = getDecodedText(property.displayName)
  let propertyLocation = handleRh3AndPropertyLocation({
    rhs3: property.rhs3,
    propertyLocation: property.location,
  })

  // AS-2627: variant_c and variant_d invert current brand ordering — XOR yields location-first across all brands
  if (
    isNovasolBrand !==
    (propertyCardV2Variant === 'variant_c' ||
      propertyCardV2Variant === 'variant_d')
  ) {
    const currentPropertyName = propertyName
    const currentPropertyLocation = propertyLocation
    propertyName = currentPropertyLocation || null
    propertyLocation = currentPropertyName || ''
  }

  if (isNovasolBrand && !datedSearch) {
    PDPUrlStartDate = null
  }

  const getDateStringFormatted = (date: Date | undefined): string | null => {
    if (!date) {
      return null
    }

    return `${formatDate(date, { day: 'numeric' })} ${formatDate(date, {
      month: 'short',
    })}`
  }

  const formattedPropertyCardProps: PropertyFormatterReturnProps = {
    brand: brand,
    code: property.code,
    propertyName,
    propertyLocation,
    images: setImageAttributes(property.images, brand),
    price: getParsedNumber(property.price),
    fromPrice: getParsedNumber(property.priceFrom),
    wasPrice: getParsedNumber(property.wasPrice),
    lengthOfStay: getParsedNumber(property.lengthOfStay) ?? PRICE_FROM_NIGHTS,
    pageUrl: setPropertyDetailLink({
      startDate: PDPUrlStartDate,
      lengthOfStay: getParsedNumber(property.lengthOfStay),
      title: getDecodedText(property.displayName),
      code: property.code,
      siteName: '',
      siteCode: '',
      pdpUrlSegment: pdpUrlSegment,
      searchParameters: searchParams,
    }),
    propertyType: formatMessage({
      id: `property-specs.accommodation-type-${property.accommodationTypeId}`,
      defaultMessage: 'Property',
    }),
    hasPetsFree: property.petsGoFree ?? false,
    hasCallout: property.isCustomerChoice ?? false,
    activeDepositScheme: setActiveDepositScheme({
      cardinality: property.cardinality,
      price: getParsedNumber(property.price),
      priceFrom: getParsedNumber(property.priceFrom),
      sleeps: getParsedNumber(property.property?.sleeps),
      deposits: deposits || null,
      startDate: datedSearch && newStartDate ? newStartDate : null,
      lengthOfStay: getParsedNumber(property.lengthOfStay) ?? 0,
      country,
      accommodationType: accommodationType as AccommodationType,
    }),
    property: {
      pets: getParsedNumber(property.property?.pets),
      sleeps: getParsedNumber(property.property?.sleeps),
      bedrooms: getParsedNumber(property.property?.bedrooms),
      bathrooms: getParsedNumber(property.property?.bathrooms),
      // TODO: These extras currently display as pure pills without mentioning "incl." as on the designs eg "Bed linen" instead of "Bed linen incl."
      includedExtras: getArrayValues(
        property.property?.includedExtras?.map(
          (extra) => extra.includedExtraName,
        ),
      ),
    },
    keySellingPoints: getKSPArray(property.USPs || []),
    startDate: newStartDate ? getDateStringFormatted(newStartDate) : null,
    guestRating: getParsedNumber(property.averageReviewScore),
    grade: getParsedNumber(property.grade),
    urgencyMessaging: !!property.urgencyNudgeMessaging,
    datedSearch: datedSearch,
  }

  const returnedProperty = setExperimentsValues({
    formattedProps: formattedPropertyCardProps,
    searchParams: searchParams,
    datedSearch: datedSearch,
    brand: brand,
  })

  return returnedProperty
}
